import * as IF from "infrontjs";
import template from "./detail.html";
import { _lcs, locale } from "../../i18n";
import { renderTranslationSwitch, translatedField } from "../general/translation-switch";

// Every *_TITLES map below holds the German source string, which is also its
// dictionary key: module constants are evaluated before I18n exists, so the
// translation happens at the point of use.
const WEEKDAYS = {
    mo : "Mo", tu : "Di", we : "Mi", th : "Do", fr : "Fr", sa : "Sa", su : "So"
};

const WEEKDAY_ORDER = [ "mo", "tu", "we", "th", "fr", "sa", "su" ];

const CATEGORY_TITLES = {
    restaurant : "Restaurant", cafe : "Café", bar : "Bar", bakery : "Bäckerei",
    winery : "Weingut", brewery : "Brauerei", beer_garden : "Biergarten",
    mountain_hut : "Berghütte", ice_cream : "Eisdiele", farm_shop : "Hofladen",
    sight : "Sehenswürdigkeit"
};

const ACCESSIBILITY_TITLES = {
    wheelchair : "Rollstuhlgerecht", step_free : "Stufenlos",
    accessible_wc : "Barrierefreies WC", hearing_loop : "Höranlage",
    service_dog : "Blindenführhund erlaubt", parking : "Parkplätze"
};

const DIETARY_TITLES = {
    vegetarian : "Vegetarisch", vegan : "Vegan", gluten_free : "Glutenfrei",
    lactose_free : "Laktosefrei", halal : "Halal", organic : "Bio",
    regional : "Regional"
};

/**
 * <place-detail> — the full record behind a row of the Gastronomie table.
 *
 * Read-only by design, with one exception: publishing. Everything a place
 * knows comes from an importer, so there is nothing here an operator would
 * type — but deciding whether a record reaches guests is exactly the decision
 * this screen exists for, and it is a single-field PATCH.
 *
 * Publishing LOCKS the record (the API's rule: any edit that is not just the
 * lock flag sets it), so the next nightly run will not quietly revert the
 * decision. The footer says so rather than leaving an operator to discover it.
 *
 * Dispatches a bubbling "place-saved" event so the host state can refresh.
 */
class PlaceDetail extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
        this._place = null;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );

        this._modal = this.querySelector( '[data-id="plc-modal"]' );
        this._title = this.querySelector( '[data-id="plc-title"]' );
        this._error = this.querySelector( '[data-id="plc-error"]' );
        this._publish = this.querySelector( '[data-id="plc-publish"]' );
        this._publishLabel = this.querySelector( '[data-id="plc-publish-label"]' );
        this._hint = this.querySelector( '[data-id="plc-hint"]' );
    }

    bind()
    {
        this.querySelector( '[data-id="plc-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="plc-cancel"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="plc-backdrop"]' ).addEventListener( "click", () => this.close() );
        this._publish.addEventListener( "click", () => this.togglePublished() );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
    }

    /* ------------------------------- Public API ------------------------------ */

    open( place, sourceName )
    {
        this._place = place;
        this._clearError();

        this._title.textContent = place.name || _lcs( "Eintrag" );
        this._set( "plc-key", [ place.slug, place.sourceId ].filter( Boolean ).join( " · " ) || "–" );

        this._renderBadges( place );
        this._renderImages( place );
        this._renderLanguageSwitch( place );
        this._renderTexts( place );
        this._renderAddress( place );
        this._renderContact( place );
        this._renderHours( place );
        this._renderAttributes( place );
        this._renderProvenance( place, sourceName );
        this._renderPublishAction( place );

        this._modal.hidden = false;
    }

    close()
    {
        this._modal.hidden = true;
        this._clearError();
    }

    /* -------------------------------- Sections ------------------------------- */

    _renderBadges( place )
    {
        const badges = [];

        badges.push( '<span class="plc-detail__badge plc-detail__badge--' + this._escape( place.status ) + '">' +
            this._escape( this._statusLabel( place.status ) ) + '</span>' );

        ( Array.isArray( place.categories ) ? place.categories : [] ).forEach( ( key ) =>
        {
            badges.push( '<span class="plc-detail__badge">' +
                this._escape( CATEGORY_TITLES[ key ] ? _lcs( CATEGORY_TITLES[ key ] ) : key ) + '</span>' );
        } );

        if ( place.isLocked )
        {
            badges.push( '<span class="plc-detail__badge plc-detail__badge--lock">' +
                '<i class="icon icon--lock"></i> ' + this._escape( _lcs( "Manuell bearbeitet" ) ) + '</span>' );
        }
        if ( place.isDuplicate )
        {
            badges.push( '<span class="plc-detail__badge plc-detail__badge--dupe">' +
                '<i class="icon icon--layers"></i> ' + this._escape( _lcs( "Dublette" ) ) + '</span>' );
        }
        if ( place.isPermanentlyClosed )
        {
            badges.push( '<span class="plc-detail__badge plc-detail__badge--closed">' +
                this._escape( _lcs( "Dauerhaft geschlossen" ) ) + '</span>' );
        }

        this.querySelector( '[data-id="plc-badges"]' ).innerHTML = badges.join( "" );
    }

    _renderImages( place )
    {
        const el = this.querySelector( '[data-id="plc-images"]' );
        const images = Array.isArray( place.images ) ? place.images : [];

        if ( images.length === 0 )
        {
            el.hidden = true;
            el.innerHTML = "";
            return;
        }

        el.innerHTML = images.slice( 0, 8 ).map( ( image ) =>
            '<figure class="plc-detail__image">' +
                '<img src="' + this._escape( image.url ) + '" alt="' + this._escape( image.alt || "" ) + '" loading="lazy" />' +
                // The credit is a licence condition of the open-data pool, not
                // decoration — it travels with every mirrored picture.
                ( image.credit
                    ? '<figcaption>' + this._escape( image.credit ) +
                      ( image.license ? ' · ' + this._escape( image.license ) : "" ) + '</figcaption>'
                    : "" ) +
            '</figure>'
        ).join( "" );
        el.hidden = false;
    }

    /**
     * The switch between the original and its machine translations. It swaps
     * the teaser, the description and the opening-hours note. The name is a
     * proper name and never translated (see core-api ContentTranslationService).
     */
    _renderLanguageSwitch( place )
    {
        this._lang = place.language || "de";
        renderTranslationSwitch( this.querySelector( '[data-id="plc-langs"]' ), place, {
            "active" : this._lang,
            "onChange" : ( lang ) =>
            {
                this._lang = lang;
                this._renderTexts( place );
                this._renderHoursNote( place );
            }
        } );
    }

    _renderTexts( place )
    {
        const teaserText = translatedField( place, this._lang, "teaser" );
        const teaser = this.querySelector( '[data-id="plc-teaser"]' );
        teaser.textContent = teaserText;
        teaser.hidden = !teaserText;

        const descriptionText = translatedField( place, this._lang, "description" );
        const description = this.querySelector( '[data-id="plc-description"]' );
        description.innerHTML = "";
        if ( descriptionText )
        {
            // textContent per paragraph: the feed's prose is plain text and
            // must not be able to inject markup.
            String( descriptionText ).split( /\n{2,}/ ).forEach( ( paragraph ) =>
            {
                const p = document.createElement( "p" );
                p.textContent = paragraph.trim();
                if ( p.textContent.length > 0 ) description.appendChild( p );
            } );
        }
        description.hidden = !descriptionText;
    }

    _renderAddress( place )
    {
        const location = place.location || {};
        const street = [ location.street, [ location.postalCode, location.city ].filter( Boolean ).join( " " ) ]
            .filter( Boolean ).join( ", " );

        this._facts( "plc-address", [
            [ _lcs( "Name" ), location.name ],
            [ _lcs( "Adresse" ), street ],
            [ _lcs( "Stadtteil" ), location.district ],
            [ _lcs( "Koordinaten" ), place.latitude != null && place.longitude != null
                ? ( +place.latitude ).toFixed( 5 ) + ", " + ( +place.longitude ).toFixed( 5 )
                : null ],
            [ _lcs( "Anfahrt" ), location.directions ]
        ] );
    }

    _renderContact( place )
    {
        const contact = place.contact || {};

        this._facts( "plc-contact", [
            [ _lcs( "Telefon" ), contact.phone ],
            [ _lcs( "E-Mail" ), contact.email ? this._link( "mailto:" + contact.email, contact.email ) : null, true ],
            [ _lcs( "Website" ), contact.website ? this._link( contact.website, contact.website ) : null, true ],
            [ _lcs( "Reservierung" ), contact.bookingUrl
                ? this._link( contact.bookingUrl, _lcs( "Reservieren" ) ) : null, true ]
        ] );
    }

    _renderHours( place )
    {
        const hours = Array.isArray( place.openingHours ) ? place.openingHours : [];

        this._facts( "plc-hours", hours.map( ( entry ) => [
            this._daysLabel( entry.days ),
            _lcs( "{time} Uhr", { time: entry.opens + " – " + entry.closes } )
        ] ) );

        this._renderHoursNote( place );
    }

    _renderHoursNote( place )
    {
        const text = translatedField( place, this._lang, "openingNote" );
        const note = this.querySelector( '[data-id="plc-hours-note"]' );
        note.textContent = text;
        note.hidden = !text;
    }

    _renderAttributes( place )
    {
        const attributes = place.attributes || {};
        const seating = attributes.seating || {};

        const seats = [
            seating.indoor ? _lcs( "{count} innen", { count: seating.indoor } ) : null,
            seating.garden ? _lcs( "{count} Garten", { count: seating.garden } ) : null,
            seating.terrace ? _lcs( "{count} Terrasse", { count: seating.terrace } ) : null
        ].filter( Boolean ).join( " · " );

        this._facts( "plc-attributes", [
            [ _lcs( "Preisniveau" ), this._priceText( attributes.priceLevel ) ],
            [ _lcs( "Küche" ), ( attributes.cuisines || [] ).join( ", " ) ],
            [ _lcs( "Ernährung" ), ( attributes.dietary || [] )
                .map( ( k ) => DIETARY_TITLES[ k ] ? _lcs( DIETARY_TITLES[ k ] ) : k ).join( ", " ) ],
            [ _lcs( "Sitzplätze" ), seats ],
            [ _lcs( "Spezialitäten" ), attributes.specialities ],
            [ _lcs( "Barrierefreiheit" ), ( place.accessibility || [] )
                .map( ( k ) => ACCESSIBILITY_TITLES[ k ] ? _lcs( ACCESSIBILITY_TITLES[ k ] ) : k ).join( ", " ) ],
            [ _lcs( "Preisangabe" ), place.pricing && place.pricing.note ]
        ] );
    }

    _renderProvenance( place, sourceName )
    {
        this._facts( "plc-provenance", [
            [ _lcs( "Quelle" ), sourceName ],
            [ _lcs( "Anbieter-ID" ), place.sourceId ],
            [ _lcs( "Zuletzt synchronisiert" ), this._dateText( place.sourceSyncedAt ) ],
            [ _lcs( "Zuletzt geändert" ), this._dateText( place.updatedAt ) ],
            [ _lcs( "Anbieter-Seite" ), place.sourceUrl
                ? this._link( place.sourceUrl, _lcs( "Original ansehen" ) ) : null, true ]
        ] );
    }

    _renderPublishAction( place )
    {
        const isPublished = place.status === "published";

        this._publishLabel.textContent = isPublished ? _lcs( "Zurückziehen" ) : _lcs( "Veröffentlichen" );
        this._publish.classList.toggle( "btn--primary", !isPublished );
        this._publish.classList.toggle( "btn--ghost", isPublished );

        this._hint.textContent = place.isLocked
            ? _lcs( "Dieser Eintrag ist gegen den Import gesperrt." )
            : _lcs( "Eine Änderung sperrt den Eintrag gegen den nächsten Import." );
    }

    /* -------------------------------- Actions -------------------------------- */

    async togglePublished()
    {
        if ( !this._place )
        {
            return;
        }

        const next = this._place.status === "published" ? "draft" : "published";

        this._publish.disabled = true;
        try
        {
            const { result } = await this.app.api.patch( "/place/platform/" + this._place.id, { "status" : next } );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected update response." );
            }

            this._place = result.data || this._place;
            this._renderBadges( this._place );
            this._renderPublishAction( this._place );

            this.app.view.toast(
                next === "published"
                    ? _lcs( "„{name}“ ist jetzt für Gäste sichtbar.", { name: this._place.name } )
                    : _lcs( "„{name}“ wurde zurückgezogen.", { name: this._place.name } ),
                "success"
            );

            this.dispatchEvent( new CustomEvent( "place-saved", {
                bubbles : true,
                detail : { place : this._place }
            } ) );
        }
        catch ( err )
        {
            this._showError( this._errorForStatus( +err?.status ) );
        }
        finally
        {
            this._publish.disabled = false;
        }
    }

    /* -------------------------------- Helpers -------------------------------- */

    /**
     * A definition list, skipping every row that has nothing to say — a fact
     * list padded with "—" is harder to read than a short one.
     */
    _facts( id, rows )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );

        const html = ( rows || [] )
            .filter( ( [ , value ] ) => value !== null && value !== undefined && value !== "" )
            .map( ( [ label, value, isHtml ] ) =>
                '<div class="plc-detail__fact">' +
                    '<dt>' + this._escape( label ) + '</dt>' +
                    '<dd>' + ( isHtml ? value : this._escape( value ) ) + '</dd>' +
                '</div>'
            ).join( "" );

        el.innerHTML = html || '<p class="plc-detail__empty">' + this._escape( _lcs( "Keine Angaben" ) ) + '</p>';
    }

    _link( href, label )
    {
        return '<a href="' + this._escape( href ) + '" target="_blank" rel="noopener noreferrer">' +
            this._escape( label ) + '</a>';
    }

    /** "Mo–Fr" for a run of consecutive days, "Sa, So" otherwise. */
    _daysLabel( days )
    {
        const list = ( Array.isArray( days ) ? days : [] )
            .filter( ( d ) => WEEKDAYS[ d ] )
            .sort( ( a, b ) => WEEKDAY_ORDER.indexOf( a ) - WEEKDAY_ORDER.indexOf( b ) );

        if ( list.length === 0 )
        {
            return "—";
        }

        const day = ( key ) => _lcs( WEEKDAYS[ key ] );

        if ( list.length === 1 )
        {
            return day( list[ 0 ] );
        }

        const first = WEEKDAY_ORDER.indexOf( list[ 0 ] );
        const isRun = list.every( ( d, index ) => WEEKDAY_ORDER.indexOf( d ) === first + index );

        return isRun
            ? day( list[ 0 ] ) + "–" + day( list[ list.length - 1 ] )
            : list.map( day ).join( ", " );
    }

    _priceText( level )
    {
        const value = +level;
        return Number.isFinite( value ) && value >= 1 && value <= 4 ? "€".repeat( value ) : null;
    }

    _statusLabel( status )
    {
        const label = ( {
            published : "Veröffentlicht", draft : "Entwurf", archived : "Archiviert",
            cancelled : "Abgesagt", postponed : "Verschoben"
        } )[ status ];
        return label ? _lcs( label ) : ( status || "—" );
    }

    _dateText( value )
    {
        if ( !value )
        {
            return null;
        }
        const date = new Date( value );
        if ( isNaN( date.getTime() ) )
        {
            return null;
        }
        return _lcs( "{time} Uhr", {
            time : date.toLocaleString( locale(), {
                day : "2-digit", month : "2-digit", year : "numeric", hour : "2-digit", minute : "2-digit"
            } )
        } );
    }

    _set( id, value )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.textContent = value;
    }

    _errorForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Die Änderung wurde abgelehnt." );
            case 401:
            case 403: return _lcs( "Keine Berechtigung für diese Aktion." );
            case 404: return _lcs( "Der Eintrag wurde nicht gefunden." );
            default:  return _lcs( "Speichern fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    _showError( message )
    {
        this._error.textContent = message;
        this._error.hidden = false;
    }

    _clearError()
    {
        this._error.textContent = "";
        this._error.hidden = true;
    }

    _escape( str )
    {
        return String( str === null || str === undefined ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }
}

customElements.define( "place-detail", PlaceDetail );
